/**
 * Mobile drawer state bridge (flag F-28).
 *
 * WHY THIS EXISTS. The drawer's show/hide is owned by the CHILD, on its own
 * `is-open` class (see src/scss/components/_mobile-nav.scss for why). But
 * Bootstrap's JS ships in the parent's main.js and binds
 * `data-bs-toggle="offcanvas"`, so pressing the toggler adds `.show` to the
 * panel and injects an `.offcanvas-backdrop` — with or without our consent.
 *
 * Two options existed: unbind Bootstrap, or consume what it emits. Consuming
 * wins. Unbinding means either stripping the data attributes (losing the
 * accessible focus handling and ESC-to-close Bootstrap already implements
 * correctly) or racing its initialisation, which is timing-dependent and
 * fails intermittently — the worst kind of defect on a gated LMS.
 *
 * So: Bootstrap remains the CONTROLLER (it owns focus, ESC, click-outside),
 * and this module mirrors its `.show` state onto the class our CSS reads.
 * If Bootstrap is ever removed, the fallback binding below still opens and
 * closes the drawer.
 *
 * @since 0.5.0
 */

const PANEL = '.bnrmp-mobile-nav__panel';
const OPEN = 'is-open';

/**
 * Mirror Bootstrap's `.show` onto our `is-open` class.
 *
 * A MutationObserver rather than Bootstrap's `shown.bs.offcanvas` events:
 * the events only fire if Bootstrap actually initialised, and the observer
 * is correct either way. It also catches a `.show` applied by anything else
 * (a future block, an editor preview) without needing to know about it.
 *
 * @param {Element} panel The drawer panel.
 * @return {void}
 */
function mirrorShowState( panel ) {
	const sync = () => {
		/*
		 * `show` is the single source of truth here, NOT `is-open`.
		 *
		 * Reading `is-open` as well looks like it would also cover the fallback
		 * path — but it latches: this function WRITES `is-open`, so once set it
		 * would read its own output and never observe a close. The fallback path
		 * is covered where it belongs, inside bindFallback itself.
		 */
		/*
		 * ── `data-bnrmp-hiding` LETS THE CLOSE START EARLY ────────────────
		 * Bootstrap keeps `.show` on the panel for the whole of its own hide
		 * sequence (measured on this theme: 188ms), so mirroring `.show`
		 * alone means our transform cannot begin until that finishes — the
		 * drawer sits still, then slides. Total measured 454ms for a 166ms
		 * animation.
		 *
		 * `hide.bs.offcanvas` sets this attribute at the START of the
		 * sequence. While it is present the panel counts as closing even
		 * though `.show` is still there, so the transform begins on the
		 * click's own frame.
		 *
		 * 🔴 A PLAIN `classList.remove()` IN THE EVENT HANDLER DOES NOT WORK
		 * and was tried first: this observer re-runs on that very class
		 * mutation, still sees `.show`, and immediately puts `is-open` back.
		 * Measured — at t=1ms the class read `show+is-open` again. The state
		 * has to be expressed somewhere this function reads, not fought over.
		 */
		const open = panel.classList.contains( 'show' )
			&& ! panel.hasAttribute( 'data-bnrmp-hiding' );
		panel.classList.toggle( OPEN, open );
		syncExpanded( panel, open );
	};
	sync();
	new MutationObserver( sync ).observe( panel, {
		attributes: true,
		attributeFilter: [ 'class' ],
	} );
}

/**
 * Keep every toggler's `aria-expanded` in step with the drawer's real state.
 *
 * 🔴 WHY THIS IS JS AND NOT JUST AN ATTRIBUTE IN header.php.
 * Bootstrap's **Collapse** and **Dropdown** maintain `aria-expanded` for you.
 * Its **Offcanvas does not** — it manages focus, ESC and the backdrop, but
 * never touches the attribute. Measured in the Phase 5 a11y pass: with
 * `aria-expanded="false"` hardcoded in the markup, the drawer opened, focus
 * moved in correctly, and the attribute stayed `false`.
 *
 * That is worse than omitting it. An absent attribute tells a screen reader
 * nothing; an attribute frozen at `false` actively asserts the menu is closed
 * while it is open. The markup keeps the initial `aria-expanded="false"` so the
 * control is correctly described before any JS runs, and this function owns
 * every value after that.
 *
 * Driven from the SAME MutationObserver that mirrors `.show`, so it is correct
 * on the Bootstrap path, on the fallback path, and on any future code that
 * opens the drawer by adding `.show` — for the reason given on mirrorShowState:
 * the observer is right whether or not Bootstrap initialised.
 *
 * @param {Element} panel The drawer panel.
 * @param {boolean} open  Whether the drawer is currently open.
 * @return {void}
 */
function syncExpanded( panel, open ) {
	if ( ! panel.id ) {
		return;
	}
	const togglers = document.querySelectorAll(
		`[data-bs-target="#${ panel.id }"], [aria-controls="${ panel.id }"]`
	);
	togglers.forEach( ( el ) => {
		// Only controls that ALREADY declare the attribute — a dismiss button
		// inside the panel is not a disclosure trigger and must not gain one.
		if ( el.hasAttribute( 'aria-expanded' ) ) {
			el.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		}
	} );
}

/**
 * Fallback open/close for when Bootstrap's Offcanvas is unavailable.
 *
 * Registered only if `window.bootstrap.Offcanvas` is missing, so it can never
 * double-toggle against Bootstrap's own handler. Deliberately minimal — it
 * restores the ability to open and close the drawer, and it restores focus to
 * the trigger on close (WCAG 2.4.3), which is the one behaviour whose absence
 * is a defect rather than a downgrade.
 *
 * @param {Element} panel The drawer panel.
 * @return {void}
 */
function bindFallback( panel ) {
	let trigger = null;

	const close = () => {
		panel.classList.remove( OPEN, 'show' );
		document.body.classList.remove( 'bnrmp-drawer-open' );
		// mirrorShowState's observer keys on `show`, which the fallback never
		// sets, so the fallback announces its own state.
		syncExpanded( panel, false );
		if ( trigger && typeof trigger.focus === 'function' ) {
			trigger.focus();
			trigger = null;
		}
	};

	document.addEventListener( 'click', ( e ) => {
		const opener = e.target.closest
			? e.target.closest( `[data-bs-target="#${ panel.id }"]` )
			: null;
		if ( opener ) {
			e.preventDefault();
			trigger = opener;
			panel.classList.add( OPEN );
			document.body.classList.add( 'bnrmp-drawer-open' );
			syncExpanded( panel, true );
			const first = panel.querySelector( 'a, button' );
			if ( first ) {
				first.focus();
			}
			return;
		}
		if ( e.target.closest && e.target.closest( '[data-bs-dismiss="offcanvas"]' ) ) {
			e.preventDefault();
			close();
		}
	} );

	document.addEventListener( 'keydown', ( e ) => {
		if ( 'Escape' === e.key && panel.classList.contains( OPEN ) ) {
			close();
		}
	} );
}

/**
 * Apply the scroll-lock class on Bootstrap's REAL events (flag F-42).
 *
 * 🔴 WHY THIS EXISTS: `bnrmp-drawer-open` was added and removed ONLY inside
 * `bindFallback`, which registers only when `window.bootstrap.Offcanvas` is
 * MISSING — and the parent theme sets `window.bootstrap` on every page
 * (clearoute-parent/src/js/main.js:14). So the class NEVER APPLIED. The CSS
 * rule was correct, harvested, styled and gate-green — and inert.
 *
 * The lock observed in testing was Bootstrap's own inline
 * `overflow:hidden; padding-right:0px` on <body>, not ours.
 *
 * The lesson worth keeping: a gate proving a CLASS is styled says nothing
 * about whether the code that applies it ever runs. Only exercising the
 * feature shows that.
 *
 * Bound to `show.bs.offcanvas` / `hidden.bs.offcanvas` because those fire on
 * the LIVE path. Bootstrap also sets its own inline lock; ours is additive and
 * survives the cases where Bootstrap's is cleared early (notably the
 * breakpoint release below, which must leave NOTHING locked).
 *
 * @param {Element} panel The drawer panel.
 * @return {void}
 */
function bindScrollLock( panel ) {
	panel.addEventListener( 'show.bs.offcanvas', () => {
		document.body.classList.add( 'bnrmp-drawer-open' );
	} );
	panel.addEventListener( 'hidden.bs.offcanvas', () => {
		document.body.classList.remove( 'bnrmp-drawer-open' );
	} );

	/*
	 * ── START THE CLOSE IMMEDIATELY (operator 2026-08-20: "little delay") ──
	 * MEASURED before this listener: from the click on the close button to our
	 * panel's own `transitionstart` was 288ms, and only THEN did the 166ms
	 * slide begin — so the drawer sat visibly still for longer than the
	 * animation itself took.
	 *
	 * Cause: `.is-open` is mirrored off Bootstrap's `.show` by the
	 * MutationObserver above, and Bootstrap does not remove `.show` until its
	 * OWN hide sequence completes. It waits for a transition on `.offcanvas`
	 * that, on this theme, does not exist (measured: no `.offcanvas`
	 * transition rule matches this panel), so that wait is dead time.
	 *
	 * `hide.bs.offcanvas` fires at the START of Bootstrap's sequence, so
	 * dropping `.is-open` here begins our transform on the same frame as the
	 * click. The observer still runs and is still the source of truth for the
	 * class; this only gets there first. Removing an absent class is a no-op,
	 * so the two cannot fight.
	 */
	panel.addEventListener( 'hide.bs.offcanvas', () => {
		panel.setAttribute( 'data-bnrmp-hiding', '' );
	} );
	panel.addEventListener( 'hidden.bs.offcanvas', () => {
		panel.removeAttribute( 'data-bnrmp-hiding' );
	} );
	// Re-opening must clear the flag even if `hidden` never fired (an
	// interrupted close), or the panel could never be marked open again.
	panel.addEventListener( 'show.bs.offcanvas', () => {
		panel.removeAttribute( 'data-bnrmp-hiding' );
	} );

	/*
	 * Move focus INTO the panel once it is open (WCAG 2.1.2, no keyboard trap
	 * — and 2.4.3, focus order).
	 *
	 * Found while regression-testing the F-41 fix: pressing Escape did NOT
	 * close the drawer. Measured — focus stayed on `.bnrmp-mobile-nav__toggler`,
	 * which is OUTSIDE the panel, and Bootstrap's Escape handler listens on
	 * the panel. With the panel focused manually, Escape closed it correctly,
	 * so the handler was fine and the FOCUS was the defect.
	 *
	 * Consequence without this: a keyboard or screen-reader user opens the
	 * drawer and their focus is still behind it, so Escape does nothing and
	 * Tab walks the page underneath — the same "no way out" shape as F-41,
	 * reached by keyboard instead of by resize.
	 *
	 * Focus the panel itself (it carries tabindex="-1") rather than its first
	 * link: that announces the dialog before its contents and matches what
	 * Bootstrap does for its own modals.
	 */
	panel.addEventListener( 'shown.bs.offcanvas', () => {
		if ( panel.contains( document.activeElement ) ) {
			return;
		}
		/*
		 * `requestAnimationFrame`, not a bare focus() call. At `shown` time the
		 * panel is still mid-transition: our own CSS transitions `visibility`
		 * alongside the transform, and an element that is still
		 * `visibility:hidden` CANNOT take focus — the call silently no-ops.
		 * Measured: focus stayed on the toggler at the handler, at +0ms and at
		 * +50ms, while the identical call made later succeeded. Deferring one
		 * frame lets the computed style settle first.
		 *
		 * 🔴 DO NOT REPLACE THIS WITH A setTimeout — "one frame" is not a guess
		 * about how long the animation takes. The CSS is
		 *     transition: transform var(--motion-base) …,
		 *                 visibility 0s linear var(--motion-base);
		 * so `visibility` is DELAYED BY THE FULL TRANSFORM DURATION and flips
		 * only after the transform ends. Bootstrap fires `shown` AT transition
		 * end, i.e. at the exact instant visibility is still `hidden`. One
		 * frame later it is `visible`, whatever the duration is.
		 *
		 * That makes this a STYLE-SETTLE, not a duration estimate — verified
		 * holding at 260ms, at 2000ms, and at the `0.01ms` reduced-motion
		 * collapse. A timeout WOULD be a duration guess and would break when
		 * --motion-base changes. Verified 2026-08-15.
		 */
		requestAnimationFrame( () => {
			if ( ! panel.contains( document.activeElement ) ) {
				panel.focus();
			}
		} );
	} );
}

/**
 * Close the drawer when the viewport crosses out of the mobile range (F-41).
 *
 * 🔴 THE DEFECT THIS FIXES IS A FUNCTIONAL LOCKOUT, not a cosmetic one.
 * `_mobile-nav.scss` hides the panel with `display:none` above 768px, but
 * nothing told Bootstrap to hide the INSTANCE. Open the drawer at 390px and
 * cross the breakpoint — rotate a tablet, unmaximize, leave responsive mode —
 * and the result was:
 *
 *   panel `display:none` but still open · backdrop still present
 *   <body> still carrying Bootstrap's `overflow:hidden` · page unscrollable
 *   toggler `display:none` · the only dismiss control 0px wide
 *   Escape cannot fire (focus is never inside a display:none panel)
 *   Tab escapes into page content (no trap while the lock is held)
 *
 * The only way out was clicking a featureless scrim. On this LMS that strands
 * a learner away from course content and the certificate download.
 *
 * THE CSS `display:none` IS CORRECT AND STAYS — its tab-order rationale is
 * sound. The bug is hiding a panel with CSS while its JS state persists.
 * Bootstrap's own `hide()` is what releases the ScrollBarHelper lock, removes
 * the backdrop and restores focus, so we ask IT to close rather than
 * unpicking its state by hand.
 *
 * `matchMedia` + `change` (not `resize`) so this fires once per crossing
 * rather than on every intermediate pixel.
 *
 * @param {Element} panel The drawer panel.
 * @return {void}
 */
function bindBreakpointRelease( panel ) {
	// Must match the `min-width: 768px` in components/_mobile-nav.scss that
	// hides the panel. If that breakpoint moves, move this with it — a
	// mismatch reopens exactly the lockout described above.
	const desktop = window.matchMedia( '(min-width: 768px)' );

	const release = () => {
		if ( ! desktop.matches ) {
			return;
		}
		const instance = window.bootstrap.Offcanvas.getInstance( panel );
		if ( instance ) {
			instance.hide();
		}
		// Belt and braces: if Bootstrap had no instance (drawer opened by the
		// fallback, or state left over from a previous script), clear our own
		// markers so the page is never left locked by us either.
		panel.classList.remove( OPEN, 'show' );
		document.body.classList.remove( 'bnrmp-drawer-open' );
	};

	if ( 'function' === typeof desktop.addEventListener ) {
		desktop.addEventListener( 'change', release );
	} else if ( 'function' === typeof desktop.addListener ) {
		desktop.addListener( release );   // Safari < 14
	}
}

/**
 * Initialise the drawer bridge.
 *
 * @return {void}
 */
export function initMobileDrawer() {
	const panel = document.querySelector( PANEL );
	if ( ! panel ) {
		return;
	}

	mirrorShowState( panel );

	const hasBootstrap =
		window.bootstrap && 'function' === typeof window.bootstrap.Offcanvas;

	if ( hasBootstrap ) {
		bindScrollLock( panel );
		bindBreakpointRelease( panel );
	} else {
		bindFallback( panel );
	}
}
